import React, { useState } from "react";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Flex, message, Upload, Button } from "antd";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { imageDb } from "../../Firebase/Config";
import secureLocalStorage from "react-secure-storage";
import imageCompression from "browser-image-compression"; // Import the compression library

// Function to read image as base64
const getBase64 = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
};

// Before upload function to check file type and size
const beforeUpload = (file) => {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error("Image must be smaller than 2MB!");
  }
  return isJpgOrPng && isLt2M;
};

const ImageUpload = () => {
  const user = JSON.parse(secureLocalStorage.getItem("profile"));
  const email = user?.results.email;
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null); // Store the file for later use
  const [imageUploaded, setImageUploaded] = useState(false); // Track if the image is uploaded

  const handleChange = async (info) => {
    if (info.file.status === "uploading") {
      setLoading(true);
    }

    if (info.file.status === "done") {
      // Compress the image before uploading
      try {
        const options = {
          maxSizeMB: 1, // Reduce to 1MB
          maxWidthOrHeight: 800, // Max width/height
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(
          info.file.originFileObj,
          options
        );

        // Convert compressed image to base64 to show preview
        getBase64(compressedFile, (url) => {
          setImageUrl(url);
          setImageFile(compressedFile); // Store the compressed file
          setImageUploaded(true); // Mark image as uploaded
        });
      } catch (error) {
        message.error("Image compression failed.");
        setLoading(false);
      }
    }
  };

  const handleSave = () => {
    if (!imageFile) {
      message.error("No image selected!");
      return;
    }

    const fileName = `${email}`; // Use email as the file name
    const storageRef = ref(imageDb, `profile_images/${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, imageFile);

    // Show progress message
    const hideMessage = message.loading("Saving image...", 0); // '0' keeps the message until explicitly dismissed

    uploadTask.on(
      "state_changed",
      () => {
        setLoading(true);
      },
      (error) => {
        hideMessage(); // Hide the progress message
        message.error("Upload failed!");
        setLoading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setLoading(false);
          hideMessage(); // Hide the progress message
          message.success("Profile image saved!");
          setImageUrl(downloadURL);
        });
      }
    );
  };

  const handleCancel = () => {
    setImageUrl(null); // Remove the image preview
    setImageFile(null); // Reset the file
    setImageUploaded(false); // Reset the image uploaded flag
  };

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
      }}
      type='button'>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        style={{
          marginTop: 8,
        }}>
        Upload
      </div>
    </button>
  );

  return (
    <Flex direction='column' gap='middle' wrap>
      <Upload
        name='avatar'
        listType='picture-card'
        className='avatar-uploader'
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={({ file, onSuccess }) => {
          setTimeout(() => onSuccess("ok"), 0); // Simulate success
        }}
        onChange={handleChange}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt='avatar'
            style={{
              width: "100%",
            }}
          />
        ) : (
          uploadButton
        )}
      </Upload>

      {imageUploaded && (
        <Flex gap={8} style={{ marginTop: 16 }}>
          <Button type='primary' onClick={handleSave}>
            Save
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </Flex>
      )}
    </Flex>
  );
};

export default ImageUpload;
